import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowRight, Star, CheckCircle, Brain, Menu, X, MessageCircle, BarChart3, Users, Sparkles, TrendingUp, MessageSquare, LineChart, Lightbulb } from '../../components/icons';
import { Linkedin, Twitter, Instagram, Facebook } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

function HeaderFR() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Accueil', href: '/fr' },
    { label: 'A propos', href: '#about' },
    { label: 'Fonctionnalites', href: '#features' },
    { label: 'FAQ', href: '#faq' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('#')) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(href);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-shadow-md' : 'bg-transparent'}`}>
      <div className="max-w-[1216px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => navigate('/fr')} className="flex items-center gap-2">
            <div className="w-10 h-10 bg-brand-90 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-brand-90">Profil Indicateur de Potentiel</span>
          </button>
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button key={link.label} onClick={() => handleNavClick(link.href)} className="text-md font-semibold text-gray-60 hover:text-brand-60 transition-colors">
                {link.label}
              </button>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/fr/login')} className="text-gray-60">Se connecter</Button>
            <Button onClick={() => navigate('/fr/login')}>Commencer</Button>
          </div>
          <button className="md:hidden p-2 text-gray-60" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-20">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <button key={link.label} onClick={() => handleNavClick(link.href)} className="block w-full text-left px-4 py-3 text-md font-semibold text-gray-60 hover:bg-gray-10 rounded-xl transition-colors">
                {link.label}
              </button>
            ))}
            <div className="pt-4 space-y-2">
              <Button variant="outline" className="w-full" onClick={() => { setIsMobileMenuOpen(false); navigate('/fr/login'); }}>Se connecter</Button>
              <Button className="w-full" onClick={() => { setIsMobileMenuOpen(false); navigate('/fr/login'); }}>Commencer</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function HeroSectionFR() {
  const navigate = useNavigate();
  return (
    <section className="pt-28 pb-16 md:pt-32 md:pb-24 px-4 md:px-8 bg-brand-5">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 relative">
            <div className="relative max-w-md mx-auto lg:mx-0">
              <div className="absolute -top-4 -left-4 w-full h-full bg-success-10 rounded-[32px] transform rotate-3" />
              <div className="relative bg-white rounded-[32px] shadow-soft-lg overflow-hidden">
                <div className="p-6 border-b border-gray-20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-brand-10 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🎯</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-40">Evaluation</p>
                      <p className="font-semibold text-gray-80">Comment vous decrivez-vous ?</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 h-2 bg-success-40 rounded-full" />
                    <div className="flex-1 h-2 bg-success-10 rounded-full" />
                    <div className="flex-1 h-2 bg-success-10 rounded-full" />
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {['J\'ai confiance en mes capacites', 'J\'accepte les nouveaux defis', 'Je me connecte facilement aux autres'].map((text, i) => (
                    <div key={i} className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${i === 0 ? 'border-success-40 bg-success-5' : 'border-gray-20 hover:border-gray-30'}`}>
                      <CheckCircle className={`w-5 h-5 ${i === 0 ? 'text-success-40' : 'text-gray-30'}`} />
                      <span className="text-gray-60 text-sm">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-soft p-4 hidden md:block">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-orange-10 rounded-full flex items-center justify-center">
                    <span className="text-lg">✨</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-40">Insights</p>
                    <p className="text-sm font-semibold text-gray-80">Analyse IA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 text-center lg:text-left">
            <Badge variant="brand" className="mb-6">
              <Star className="w-4 h-4 mr-1 fill-brand-40 text-brand-40" />
              Evaluation de Personnalite
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-heading-xl font-bold text-brand-90 mb-6 leading-tight">
              Decouvrez votre veritable potentiel.{' '}
              <span className="text-success-40">A tout moment, partout.</span>
            </h1>
            <p className="text-lg md:text-paragraph-xl text-gray-60 mb-8 max-w-xl mx-auto lg:mx-0">
              Les evaluations de personnalite faconnent la clarte quotidienne, pas seulement les grands tournants. Le Profil Indicateur de Potentiel est votre partenaire pour l'epanouissement, la croissance et la reussite professionnelle. Concu pour vous aider a comprendre, gerer et developper votre parcours de potentiel personnel.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button size="lg" onClick={() => navigate('/fr/login')} className="gap-2">
                Commencer l'evaluation
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button variant="secondary" size="lg" onClick={() => navigate('/fr/login')}>
                Voir les resultats exemples
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBarFR() {
  const avatars = [
    'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100',
    'https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=100',
  ];
  return (
    <section className="py-6 px-4 md:px-8 bg-brand-40">
      <div className="max-w-[1216px] mx-auto flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
        <div className="flex -space-x-3">
          {avatars.map((avatar, i) => (
            <div key={i} className="w-10 h-10 rounded-full border-2 border-white overflow-hidden">
              <img src={avatar} alt={`Utilisateur ${i + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <p className="text-white font-semibold text-lg">
          Approuve par <span className="text-brand-10">1 000+</span> personnes dans le monde
        </p>
      </div>
    </section>
  );
}

function BenefitsSectionFR() {
  const benefits = [
    {
      id: 'ai-companion',
      badge: 'Intelligence Artificielle',
      title: 'Compagnon IA Conscient',
      description: 'Notre IA analyse vos reponses et offre des insights personnalises pour vous aider a comprendre vos pensees et comportements.',
      features: [
        { icon: MessageCircle, label: 'Developpement Personnel' },
        { icon: Brain, label: 'Insights Profonds' },
      ],
      image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600',
      imageAlt: 'Tableau de bord Analyse IA',
      reverse: false,
    },
    {
      id: 'metrics',
      badge: 'Analytique',
      title: 'Metriques de Personnalite',
      description: 'Recevez des recommandations douces et actionnables adaptees a vos besoins - de la comprehension de vos 12 caracteristiques cles au suivi de vos niveaux harmoniques.',
      features: [
        { icon: BarChart3, label: '12 Caracteristiques' },
        { icon: TrendingUp, label: 'Niveaux Harmoniques' },
      ],
      image: 'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=600',
      imageAlt: 'Graphique des Metriques de Personnalite',
      reverse: true,
    },
    {
      id: 'coach',
      badge: 'Accompagnement',
      title: 'Rendez-vous avec un Coach',
      description: 'Parfois vous avez besoin de plus que l\'auto-reflexion. Connectez-vous avec des coachs certifies qui peuvent vous aider a interpreter vos resultats et creer des plans de developpement actionnables.',
      features: [
        { icon: Users, label: 'Guidage Expert' },
        { icon: Sparkles, label: 'Plans Personnalises' },
      ],
      image: 'https://images.pexels.com/photos/5717546/pexels-photo-5717546.jpeg?auto=compress&cs=tinysrgb&w=600',
      imageAlt: 'Seance de Coaching',
      reverse: false,
    },
  ];

  return (
    <section id="features" className="py-20 md:py-28 px-4 md:px-8 bg-white">
      <div className="max-w-[1216px] mx-auto">
        <div className="text-center mb-16">
          <Badge variant="default" className="mb-4">Avantages</Badge>
          <h2 className="text-3xl md:text-heading-md font-bold text-brand-90 mb-4">
            Decouvrez les avantages concrets que nos utilisateurs apprecient le plus.
          </h2>
          <p className="text-paragraph-xl text-gray-60 max-w-2xl mx-auto">
            Le Profil Indicateur de Potentiel vous offre des outils pour explorer vos forces en profondeur, surmonter les defis et trouver la clarte en quelques minutes par jour.
          </p>
        </div>
        <div className="space-y-24">
          {benefits.map((benefit) => (
            <div key={benefit.id} className={`flex flex-col gap-12 items-center ${benefit.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
              <div className="flex-1 w-full">
                <div className="relative">
                  <div className="absolute inset-0 bg-success-10 rounded-[32px] transform rotate-2" />
                  <div className="relative rounded-[32px] overflow-hidden shadow-soft-lg">
                    <img src={benefit.image} alt={benefit.imageAlt} className="w-full h-[300px] md:h-[400px] object-cover" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-soft p-4 hidden md:flex items-center gap-3">
                    <div className="w-10 h-10 bg-success-10 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-success-40" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-80">Insights Verifies</p>
                      <p className="text-xs text-gray-40">Methodes fondees sur la science</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 w-full">
                <Badge variant={benefit.id === 'ai-companion' ? 'teal' : benefit.id === 'metrics' ? 'orange' : 'default'} className="mb-4">
                  {benefit.badge}
                </Badge>
                <h3 className="text-2xl md:text-heading-sm font-bold text-brand-90 mb-4">{benefit.title}</h3>
                <p className="text-paragraph-lg text-gray-60 mb-6">{benefit.description}</p>
                <div className="flex flex-wrap gap-4">
                  {benefit.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2 bg-gray-10 rounded-full">
                      <feature.icon className="w-4 h-4 text-success-40" />
                      <span className="text-sm font-medium text-gray-60">{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactSectionFR() {
  const stats = [
    { value: '92%', label: 'Conscience de soi amelioree' },
    { value: '7.2x', label: 'Taux de croissance' },
    { value: '120K', label: 'Evaluations completees' },
  ];
  return (
    <section className="py-16 md:py-20 px-4 md:px-8 bg-success-40">
      <div className="max-w-[1216px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-heading-sm font-bold text-white mb-2">Notre Impact Jusqu'ici</h2>
          <p className="text-success-10 text-paragraph-lg max-w-xl mx-auto">
            Le Profil Indicateur de Potentiel aide des milliers de personnes a decouvrir leur veritable potentiel et a obtenir des resultats exceptionnels dans leur vie personnelle et professionnelle.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-5xl md:text-6xl font-bold text-white mb-2">{stat.value}</p>
              <p className="text-success-10 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSectionFR() {
  const features = [
    { icon: Brain, title: 'Chat IA Personnalite 24/7', description: 'Discutez avec notre compagnon IA jour et nuit - obtenez des insights en temps reel sur vos traits de personnalite et opportunites de croissance.', color: 'success' },
    { icon: MessageSquare, title: 'Suivi Quotidien de Progres', description: 'Des bilans quotidiens rapides vous maintiennent conscient de vos progres et vous aident a garder l\'elan dans votre parcours de developpement personnel.', color: 'orange' },
    { icon: Lightbulb, title: 'Insights et Conseils Personnalises', description: 'Recevez des recommandations sur mesure basees sur votre profil unique pour vous aider a developper de nouvelles competences et surmonter les defis.', color: 'teal' },
    { icon: LineChart, title: 'Analytique de Progres', description: 'Des tableaux de bord visuels montrent votre croissance au fil du temps, vous aidant a celebrer vos accomplissements et identifier les domaines d\'amelioration continue.', color: 'brand' },
  ];

  return (
    <section id="about" className="py-20 md:py-28 px-4 md:px-8 bg-brand-5">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="lg:w-1/3">
            <Badge variant="default" className="mb-4">Fonctionnalites</Badge>
            <h2 className="text-3xl md:text-heading-md font-bold text-brand-90 mb-4">Des fonctionnalites concues pour votre croissance</h2>
            <p className="text-paragraph-lg text-gray-60">
              Explorez des outils qui combinent la decouverte de soi et la reflexion guidee pour construire des habitudes plus saines et un meilleur etat d'esprit.
            </p>
          </div>
          <div className="lg:w-2/3 grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-3xl p-6 border border-gray-20 hover:shadow-soft-lg transition-shadow">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${feature.color === 'success' ? 'bg-success-10' : feature.color === 'orange' ? 'bg-orange-10' : feature.color === 'teal' ? 'bg-teal-10' : 'bg-brand-10'}`}>
                  <feature.icon className={`w-6 h-6 ${feature.color === 'success' ? 'text-success-40' : feature.color === 'orange' ? 'text-orange-50' : feature.color === 'teal' ? 'text-teal-50' : 'text-brand-60'}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-80 mb-2">{feature.title}</h3>
                <p className="text-paragraph-sm text-gray-60">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSectionFR() {
  const faqs = [
    { question: 'Est-ce un remplacement de la therapie ?', answer: 'Non, le Profil Indicateur de Potentiel ne remplace pas la therapie professionnelle ou le traitement de sante mentale. C\'est un outil de decouverte de soi concu pour vous aider a comprendre vos traits de personnalite, vos forces et vos domaines de croissance. Si vous rencontrez des difficultes de sante mentale, veuillez consulter un professionnel de sante qualifie.' },
    { question: 'Combien de temps dure l\'evaluation ?', answer: 'L\'evaluation complete du Profil Indicateur de Potentiel prend generalement 15 a 20 minutes. Nous vous recommandons de trouver un moment calme ou vous pouvez vous concentrer sans distractions pour obtenir les resultats les plus precis.' },
    { question: 'Mes donnees personnelles sont-elles en securite ?', answer: 'Oui, la confidentialite et la securite de vos donnees sont nos priorites absolues. Nous utilisons un chiffrement aux normes de l\'industrie et ne partageons jamais vos informations personnelles avec des tiers. Vos resultats d\'evaluation sont stockes en toute securite et accessibles uniquement par vous et les coachs avec lesquels vous choisissez de les partager.' },
    { question: 'Puis-je utiliser le Profil Indicateur de Potentiel gratuitement ?', answer: 'Oui, nous offrons une experience d\'evaluation gratuite qui comprend votre profil de personnalite de base. Les fonctionnalites premium comme l\'analyse IA detaillee, les connexions avec des coachs et le suivi de progres sont disponibles via nos plans d\'abonnement.' },
    { question: 'Sur quels appareils puis-je l\'utiliser ?', answer: 'Le Profil Indicateur de Potentiel est une application web qui fonctionne sur tout appareil disposant d\'un navigateur web moderne, y compris les ordinateurs de bureau, les ordinateurs portables, les tablettes et les smartphones. Votre progression se synchronise automatiquement sur tous vos appareils.' },
  ];

  return (
    <section id="faq" className="py-20 md:py-28 px-4 md:px-8 bg-white">
      <div className="max-w-[800px] mx-auto">
        <div className="text-center mb-12">
          <Badge variant="brand" className="mb-4">FAQ</Badge>
          <h2 className="text-3xl md:text-heading-md font-bold text-brand-90 mb-4">Questions Frequemment Posees</h2>
          <p className="text-paragraph-xl text-gray-60">Vous avez des questions ? Consultez d'abord ci-dessous.</p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left text-lg">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-paragraph-md">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function CTASectionFR() {
  const navigate = useNavigate();
  return (
    <section className="py-20 md:py-28 px-4 md:px-8 bg-brand-40">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1 relative hidden lg:block">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute inset-0 bg-brand-60/30 rounded-[32px] transform -rotate-3" />
              <div className="relative bg-white rounded-[32px] shadow-soft-lg p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-success-10 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-80">Votre Parcours de Croissance</p>
                    <p className="text-sm text-gray-40">Personnalise pour vous</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-success-5 rounded-xl">
                    <div className="w-8 h-8 bg-success-40 rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
                    <span className="text-sm text-gray-60">Completer l'evaluation</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-10 rounded-xl">
                    <div className="w-8 h-8 bg-gray-30 rounded-full flex items-center justify-center text-gray-60 text-sm font-bold">2</div>
                    <span className="text-sm text-gray-40">Obtenir les insights IA</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-10 rounded-xl">
                    <div className="w-8 h-8 bg-gray-30 rounded-full flex items-center justify-center text-gray-60 text-sm font-bold">3</div>
                    <span className="text-sm text-gray-40">Se connecter avec un coach</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-3xl md:text-heading-md font-bold text-white mb-4">Prenez soin de votre croissance - Commencez aujourd'hui</h2>
            <p className="text-paragraph-xl text-brand-10 mb-8 max-w-xl mx-auto lg:mx-0">
              N'attendez pas pour faire le premier pas. Votre parcours vers la decouverte de soi commence par une evaluation simple et fondee sur la science, disponible maintenant - a tout moment, sur tout appareil.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button variant="secondary" size="lg" onClick={() => navigate('/fr/login')} className="gap-2 bg-white text-brand-90 hover:bg-gray-10">
                Commencer maintenant
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FooterFR() {
  const navigate = useNavigate();
  const handleNavClick = (href: string) => {
    if (href.startsWith('#')) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    } else if (href.startsWith('/')) {
      navigate(href);
    }
  };

  const navLinks = [
    { label: 'Accueil', href: '/fr' },
    { label: 'A propos', href: '#about' },
    { label: 'Fonctionnalites', href: '#features' },
    { label: 'FAQ', href: '#faq' },
  ];

  const socialLinks = [
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
  ];

  return (
    <footer className="py-16 md:py-24 px-4 md:px-8 bg-brand-5">
      <div className="max-w-[1216px] mx-auto">
        <div className="flex flex-col lg:flex-row justify-between gap-12 mb-12">
          <div className="max-w-md">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-brand-90 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-brand-90">Profil Indicateur de Potentiel</span>
            </div>
            <p className="text-paragraph-lg text-gray-60 mb-6">
              Construire un etat d'esprit de croissance commence par se comprendre soi-meme - et le Profil Indicateur de Potentiel est la pour vous guider a chaque etape.
            </p>
            <nav className="flex flex-wrap gap-6">
              {navLinks.map((link) => (
                <button key={link.label} onClick={() => handleNavClick(link.href)} className="text-md font-semibold text-gray-60 hover:text-brand-60 transition-colors">
                  {link.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
        <Separator className="bg-gray-20" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8">
          <p className="text-sm text-gray-40">Copyright 2025 Profil Indicateur de Potentiel, Tous Droits Reserves.</p>
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a key={social.label} href={social.href} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-10 text-gray-60 hover:bg-brand-10 hover:text-brand-60 transition-colors" aria-label={social.label}>
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export function HomePageFR() {
  return (
    <div className="min-h-screen bg-brand-5 font-urbanist">
      <HeaderFR />
      <main>
        <HeroSectionFR />
        <TrustBarFR />
        <BenefitsSectionFR />
        <ImpactSectionFR />
        <FeaturesSectionFR />
        <FAQSectionFR />
        <CTASectionFR />
      </main>
      <FooterFR />
    </div>
  );
}
